import { useLanguage } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { AlertCircle, Info } from "lucide-react";
import {
  ProductEntry,
  EXTRACTION_PRODUCT_TYPE_OPTIONS,
  TRANSFORMATION_PRODUCT_TYPE_OPTIONS,
  EXTRACTION_FUNCTIONAL_UNIT_OPTIONS,
  TRANSFORMATION_FUNCTIONAL_UNIT_OPTIONS,
  MaterialUsage,
} from "@/types/product/productStep.types";
import { useData } from "@/hooks/useData";
import { MaterialDocWithId } from "@/types/material";

interface CommonFormFieldsProps {
  formEntry: ProductEntry;
  onFormEntryChange: (entry: ProductEntry) => void;
  touched: Record<string, boolean>;
  errors: Record<string, string>;
  onFieldBlur: (field: string) => void;
  activityType?: string;
  availableMaterialIds?: string[]; // Materials from annual consumption
}

export const CommonFormFields = ({
  formEntry,
  onFormEntryChange,
  touched,
  errors,
  onFieldBlur,
  activityType,
  availableMaterialIds = [], // Default to empty array if not provided
}: CommonFormFieldsProps) => {
  const { t } = useLanguage();

  // Fetch materials for the multiselect
  const { data: materials, loading: materialsLoading } = useData<MaterialDocWithId>({
    collectionName: "setup_materials",
  });

  // Get IDs of materials already used in this product
  const usedMaterialIds = formEntry.materialsUsed.map((used) => used.materialId);

  // Filter materials to only show those from annual consumption OR those already selected in this product
  const activeMaterials = (materials || []).filter((material) => {
    // Always include materials that are already selected in this product
    if (usedMaterialIds.includes(material.id)) {
      return true;
    }

    // If no annual consumption materials are specified, show all materials (fallback)
    if (availableMaterialIds.length === 0) {
      return true;
    }

    // Otherwise, only show materials from annual consumption (by name)
    // Use case-insensitive comparison to handle potential mismatches
    const isAvailable = availableMaterialIds.some((availableName) => {
      if (typeof availableName === "string" && typeof material.name === "string") {
        return availableName.toLowerCase().trim() === material.name.toLowerCase().trim();
      }
      return false;
    });

    return isAvailable;
  });

  // Material management functions
  const addMaterial = (materialId: string) => {
    const material = activeMaterials.find((m) => m.id === materialId);
    if (!material) return;

    const existingIndex = formEntry.materialsUsed.findIndex((m) => m.materialId === materialId);
    if (existingIndex >= 0) {
      return;
    }

    const availableUnits = material.units || ["unit"];
    const defaultUnit = Array.isArray(availableUnits) ? availableUnits[0] || "unit" : "unit";

    // Calculate the quantity based on annual consumption and product allocation
    // This will be updated when the product is saved with proper allocation ratios
    const newMaterialUsage: MaterialUsage = {
      materialId: material.id,
      materialName: material.name,
      quantity: 0, // Will be auto-calculated when product is saved
      unit: defaultUnit,
    };

    onFormEntryChange({
      ...formEntry,
      materialsUsed: [...formEntry.materialsUsed, newMaterialUsage],
    });
  };

  const removeMaterial = (materialId: string) => {
    onFormEntryChange({
      ...formEntry,
      materialsUsed: formEntry.materialsUsed.filter((m) => m.materialId !== materialId),
    });
  };

  const handleInputChange = (field: string, value: any) => {
    if (field === "functionalUnitQuantity") {
      onFormEntryChange({
        ...formEntry,
        functionalUnit: {
          ...formEntry.functionalUnit,
          quantity: value,
        },
      });
    } else if (field === "functionalUnitUnit") {
      onFormEntryChange({
        ...formEntry,
        functionalUnit: {
          ...formEntry.functionalUnit,
          unit: value,
        },
      });
    } else if (field === "thickness") {
      onFormEntryChange({
        ...formEntry,
        functionalUnit: {
          ...formEntry.functionalUnit,
          thickness: value,
        },
      });
    } else {
      onFormEntryChange({
        ...formEntry,
        [field]: value,
      });
    }
  };

  // Get the appropriate product type options based on activity type
  const getProductTypeOptions = () => {
    if (activityType === "transformation" || activityType === "extraction_and_transformation") {
      return TRANSFORMATION_PRODUCT_TYPE_OPTIONS;
    } else {
      return EXTRACTION_PRODUCT_TYPE_OPTIONS;
    }
  };

  // Get the appropriate functional unit options based on activity type
  const getFunctionalUnitOptions = () => {
    if (activityType === "transformation" || activityType === "extraction_and_transformation") {
      return TRANSFORMATION_FUNCTIONAL_UNIT_OPTIONS;
    } else {
      return EXTRACTION_FUNCTIONAL_UNIT_OPTIONS;
    }
  };

  return (
    <div className="space-y-6">
      {/* Product Name */}
      <div className="space-y-2">
        <Label htmlFor="productName" className={`font-medium ${errors.name ? "text-red-500" : "text-gray-700"}`}>
          {t("products.productName")} *
        </Label>
        <Input
          id="productName"
          value={formEntry.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          onBlur={() => onFieldBlur("name")}
          placeholder={t("products.productNamePlaceholder")}
          className={`h-11 ${touched.name && errors.name ? "border-red-500" : "border-gray-300"}`}
        />
        {touched.name && errors.name && (
          <div className="flex items-center gap-1 text-red-500 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{errors.name}</span>
          </div>
        )}
      </div>

      {/* Product Type */}
      <div className="space-y-2">
        <Label htmlFor="productType" className={`font-medium ${errors.productType ? "text-red-500" : "text-gray-700"}`}>
          {t("products.productType")} *
        </Label>
        <Select value={formEntry.productType} onValueChange={(value) => handleInputChange("productType", value)}>
          <SelectTrigger
            className={`h-11 ${touched.productType && errors.productType ? "border-red-500" : "border-gray-300"}`}
          >
            <SelectValue placeholder={t("products.selectProductType")} />
          </SelectTrigger>
          <SelectContent>
            {getProductTypeOptions().map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {touched.productType && errors.productType && (
          <div className="flex items-center gap-1 text-red-500 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{errors.productType}</span>
          </div>
        )}
      </div>

      {/* Functional Unit */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label
            htmlFor="functionalUnit"
            className={`font-medium ${
              errors.functionalUnitQuantity || errors.functionalUnitUnit ? "text-red-500" : "text-gray-700"
            }`}
          >
            {t("products.functionalUnit")} *
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>{t("products.functionalUnitExplanation")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              id="functionalUnit"
              type="number"
              min="0"
              step="0.01"
              value={formEntry.functionalUnit.quantity || ""}
              onChange={(e) => {
                const inputValue = e.target.value;
                // Allow any input that could potentially be a valid number
                handleInputChange("functionalUnitQuantity", inputValue);
              }}
              onBlur={() => onFieldBlur("functionalUnitQuantity")}
              placeholder="1.00"
              className={`h-11 ${
                touched.functionalUnitQuantity && errors.functionalUnitQuantity ? "border-red-500" : "border-gray-300"
              }`}
            />
          </div>
          <div className="w-20">
            <Select
              value={formEntry.functionalUnit.unit}
              onValueChange={(value) => handleInputChange("functionalUnitUnit", value)}
            >
              <SelectTrigger
                className={`h-11 ${
                  touched.functionalUnitUnit && errors.functionalUnitUnit ? "border-red-500" : "border-gray-300"
                }`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getFunctionalUnitOptions().map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {((touched.functionalUnitQuantity && errors.functionalUnitQuantity) ||
          (touched.functionalUnitUnit && errors.functionalUnitUnit)) && (
          <div className="flex items-center gap-1 text-red-500 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{errors.functionalUnitQuantity || errors.functionalUnitUnit}</span>
          </div>
        )}
      </div>

      {/* Thickness Field - Only show for m² products */}
      {formEntry.functionalUnit.unit === "m2" && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="thickness" className="font-medium text-gray-700">
              {t("products.thickness")} *
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>{t("products.thicknessExplanation")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="relative">
            <Input
              id="thickness"
              type="number"
              min="0"
              step="0.1"
              value={formEntry.functionalUnit.thickness || ""}
              onChange={(e) => {
                const inputValue = e.target.value;
                // Allow any input that could potentially be a valid number
                handleInputChange("thickness", inputValue);
              }}
              placeholder="2"
              className="h-11 pr-12 border-gray-300"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none">
              cm
            </span>
          </div>
          <p className="text-xs text-gray-500">{t("products.thicknessNote")}</p>
        </div>
      )}

      {/* Annual Production */}
      <div className="space-y-2">
        <Label
          htmlFor="annualProduction"
          className={`font-medium ${errors.annualProduction ? "text-red-500" : "text-gray-700"}`}
        >
          {t("products.quantityProducedInStudyYear")} *
        </Label>
        <div className="relative">
          <Input
            id="annualProduction"
            type="number"
            min="0"
            step="1"
            value={formEntry.annualProduction || ""}
            onKeyDown={(e) => {
              // Block non-integer keys
              if (['.', ',', '-'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const inputValue = e.target.value;
              // Allow onlyinteger values
              if (inputValue === "" || /^\d+$/.test(inputValue)) {
                handleInputChange("annualProduction", inputValue);
              }
            }}
            onBlur={() => onFieldBlur("annualProduction")}
            placeholder="0"
            className={`h-11 pr-16 ${
              touched.annualProduction && errors.annualProduction ? "border-red-500" : "border-gray-300"
            }`}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none">
            un
          </span>
        </div>
        {touched.annualProduction && errors.annualProduction && (
          <div className="flex items-center gap-1 text-red-500 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{errors.annualProduction}</span>
          </div>
        )}
      </div>

      {/* Product Revenue */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label
            htmlFor="productRevenue"
            className={`font-medium ${errors.productRevenue ? "text-red-500" : "text-gray-700"}`}
          >
            {t("products.productRevenue")} *
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <p>{t("products.productRevenueTooltip")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="relative">
          <Input
            id="productRevenue"
            type="number"
            min="0"
            step="0.01"
            value={formEntry.productRevenue || ""}
            onChange={(e) => {
              const inputValue = e.target.value;
              // Allow any input that could potentially be a valid number
              handleInputChange("productRevenue", inputValue);
            }}
            onBlur={() => onFieldBlur("productRevenue")}
            placeholder="0.00"
            className={`h-11 pr-12 ${
              touched.productRevenue && errors.productRevenue ? "border-red-500" : "border-gray-300"
            }`}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium pointer-events-none">
            €
          </span>
        </div>
        {touched.productRevenue && errors.productRevenue && (
          <div className="flex items-center gap-1 text-red-500 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{errors.productRevenue}</span>
          </div>
        )}
      </div>

      {/* Materials Used */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Label className={`font-medium ${errors.materialsUsed ? "text-red-500" : "text-gray-700"}`}>
            {t("products.materialsUsedInProduction")} *
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <p>{t("products.materialsAutoAllocationTooltip")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {materialsLoading ? (
          <div className="text-sm text-gray-500 p-3 border border-gray-300 rounded-md bg-gray-50">
            {t("common.loading")}
          </div>
        ) : (
          <div className="space-y-3">
            {/* Information Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-800">{t("products.autoAllocationExplanation")}</p>
              </div>
            </div>

            {/* Selected Materials Count */}
            {formEntry.materialsUsed.length > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                <span className="text-sm text-gray-700">
                  {formEntry.materialsUsed.length === 1
                    ? t("products.materialSelected")
                    : t("products.materialsSelected")}
                </span>
                <span className="bg-teal-100 text-teal-800 text-xs px-2 py-1 rounded-full font-medium">
                  {formEntry.materialsUsed.length}
                </span>
              </div>
            )}

            {/* Materials Pills Selection */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {activeMaterials.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm font-medium">Nenhum material disponível</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {availableMaterialIds.length === 0
                      ? "Adicione materiais no passo 'Consumos Anuais' primeiro"
                      : "Nenhum material encontrado nos consumos anuais"}
                  </p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 p-4 bg-gray-50">
                  {activeMaterials.map((material) => {
                    const isSelected = formEntry.materialsUsed.some((used) => used.materialId === material.id);
                    return (
                      <button
                        key={material.id}
                        type="button"
                        onClick={() => {
                          if (isSelected) {
                            removeMaterial(material.id);
                          } else {
                            addMaterial(material.id);
                          }
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 ${
                          isSelected
                            ? "bg-teal-600 text-white hover:bg-teal-700"
                            : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                        }`}
                      >
                        {material.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {touched.materialsUsed && errors.materialsUsed && (
          <div className="flex items-center gap-1 text-red-500 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{errors.materialsUsed}</span>
          </div>
        )}
      </div>
    </div>
  );
};
